import { useState } from 'react';
import { Coordinates, Location } from '../types/location';
import { addLocation, updateLocation, deleteLocation } from '../api/locationService';

type ModalMode = 'new' | 'edit';

export function useMarkerModal(
    markers: Location[],
    setMarkers: (markers: Location[]) => void,
) {
    const [show, setShow] = useState(false);
    const [mode, setMode] = useState<ModalMode>('new');
    const [currentCoords, setCurrentCoords] = useState<Coordinates | null>(null);
    const [editingLocation, setEditingLocation] = useState<Location | null>(null);

    function showModal(coords: Coordinates) {
        setMode('new');
        setCurrentCoords(coords);
        setEditingLocation(null);
        setShow(true);
    }

    function editMarker(location: Location) {
        setMode('edit');
        setEditingLocation(location);
        setCurrentCoords(null);
        setShow(true);
    }

    function closeModal() {
        if (mode === 'new' && currentCoords) {
            setMarkers(markers.filter(m =>
                !(m.latitude === currentCoords.lat &&
                  m.longitude === currentCoords.long)
            ));
        }
        setShow(false);
        setCurrentCoords(null);
        setEditingLocation(null);
    }

    function handleCommentUpdate(comment: string) {
        if (mode === 'edit' && editingLocation?.id) {
            updateLocation(editingLocation.id, comment)
                .then((updated) => {
                    setMarkers(markers.map(m =>
                        m.id === editingLocation.id ? { ...m, comment: updated.comment } : m
                    ));
                })
                .catch(console.error);
        } else if (mode === 'new' && currentCoords) {
            addLocation({
                latitude: currentCoords.lat,
                longitude: currentCoords.long,
                comment: comment,
                sleep_day: new Date().toISOString().slice(0, 10),
            })
                .then((created) => {
                    setMarkers(markers.map(m =>
                        (m.latitude === currentCoords.lat &&
                         m.longitude === currentCoords.long && !m.id)
                            ? { ...m, id: created.id, comment }
                            : m
                    ));
                })
                .catch(console.error);
        }

        setShow(false);
        setCurrentCoords(null);
        setEditingLocation(null);
    }

    function deleteMarker(id: number) {
        deleteLocation(id)
            .then(() => {
                setMarkers(markers.filter(m => m.id !== id));
            })
            .catch(console.error);
    }

    return {
        show,
        showModal,
        closeModal,
        handleCommentUpdate,
        editMarker,
        deleteMarker,
        initialComment: mode === 'edit' ? (editingLocation?.comment ?? '') : undefined,
    };
}
